import type { TFunction } from 'i18next';

/**
 * The backend's own message behind a failed mutation, or a fallback.
 *
 * `axiosBaseQuery` formats a rejection as `{ status, data }`, where `data` is
 * the parsed body when there was one and the axios message string when there
 * was not. So a `data.message` is the server explaining itself, and anything
 * else is a shape we cannot read — which is what the fallback is for.
 *
 * Showing the server's message rather than one generic line is the point
 * (#2255, #2258): "Auslastung konnte nicht geladen werden" for every cause cost
 * three sessions a round and produced a wrong attribution.
 *
 * **But `data.message` is written in English by a server with no locale**, so a
 * German user adding a colleague was shown an English sentence. Endpoints that
 * refuse for a known reason therefore also send a stable `code`, and this
 * resolves it against `api_errors.<code>` when a `t` is supplied. Order is
 * deliberate: translated code → server message → caller's fallback. A code with
 * no key falls through to the English sentence rather than rendering the raw
 * key, which is the failure `fallbackLng: 'de'` produces otherwise.
 *
 * Any extra fields on the body (`limit`, `status`, …) are passed as
 * interpolation values, so a translation can say *how many* users the plan
 * allows rather than "the limit was reached".
 *
 * The codes are defined once, on the server, in
 * `new-backend/admin/src/lib/accountUserErrors.ts`; `src/i18n/apiErrorKeys.test.ts`
 * pins the two copies together.
 *
 * This was five byte-identical copies — `AccountPage`, `CheckInCheckOutSection`,
 * `UsersSection`, `UserFormDialog`, `CodeDoorFormDialog` — each a private const
 * in its own file. One definition, imported.
 */
export const errorMessageFrom = (
    err: unknown,
    fallback: string,
    t?: TFunction,
): string => {
    const e = err as { data?: { message?: string, code?: string } | string };
    if (!e || typeof e.data !== 'object' || !e.data) return fallback;

    const { message, code, ...params } = e.data;

    if (code && t) {
        // `defaultValue: ''` distinguishes "no such key" from a translation, so
        // a missing key can fall through instead of being rendered verbatim.
        const translated = t(`api_errors.${code}`, { ...params, defaultValue: '' });
        if (translated) return translated;
    }

    return message || fallback;
};
